import { useSyncExternalStore } from "react";

/** A few screens don't justify a router dependency: this tracks location via the History API. */
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());
window.addEventListener("popstate", notify);

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function navigate(to: string, options: { replace?: boolean } = {}) {
  if (to === window.location.pathname + window.location.search) return;
  window.history[options.replace ? "replaceState" : "pushState"](null, "", to);
  notify();
}

export function useLocation() {
  const href = useSyncExternalStore(subscribe, () => window.location.pathname + window.location.search + window.location.hash);
  const url = new URL(href, window.location.origin);
  return { path: url.pathname, params: url.searchParams, hash: url.hash };
}

/** Update some query params on the current path. `null` removes a param. */
export function setParams(updates: Record<string, string | null>, options: { replace?: boolean } = {}) {
  const url = new URL(window.location.href);
  for (const [key, value] of Object.entries(updates)) {
    if (value === null || value === "") url.searchParams.delete(key);
    else url.searchParams.set(key, value);
  }
  navigate(url.pathname + url.search, options);
}

export function Link({ to, children, className, onClick }: { to: string; children: React.ReactNode; className?: string; onClick?: () => void }) {
  return (
    <a
      href={to}
      className={className}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        onClick?.();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}
